import { Injectable, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import {
  loadCurrentUser,
  loadCurrentUserFailure,
  loadCurrentUserSuccess,
  startImpersonation,
  startImpersonationFailure,
  startImpersonationSuccess,
  stopImpersonation,
  stopImpersonationFailure,
  stopImpersonationSuccess,
  switchCountry,
  switchCountryFailure,
  switchCountrySuccess,
  switchLanguage,
} from './current-user.actions';
import { FedevAuthService, IFedevAuthUserProfile } from '@bmw-fedev/auth';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { UsersService } from '../../admin/users/users.service';
import { of } from 'rxjs';
import { CurrentUserWEBEam, IUserProfile } from './current-user.model';
import { IUser, IUserRequest } from '../../admin/users/users.models';
import { dispatchViewableQueueSummaries } from '../../shared/working-queue/working-queue-permissions';
import { selectActiveLanguage } from './current-user.selectors';

@Injectable()
export class CurrentUserEffects {
  private readonly actions$ = inject(Actions);
  private readonly authService = inject(FedevAuthService);
  private readonly usersService = inject(UsersService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);

  private readonly mapUserData = (
    input: CurrentUserWEBEam
  ): IUserRequest | IUser => {
    return {
      qorCNumber: input.sub || '',
      firstName: input.given_name || '',
      lastName: input.family_name || '',
      email: input.email || '',
      phone: input.phonenumber || null,
      departmentCode: input.departmentnumber || '',
    } as IUser;
  };

  private readonly userProfile$ = toObservable(this.authService.userProfile);

  loadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCurrentUser),
      exhaustMap(() =>
        this.userProfile$.pipe(
          filter(
            (profile): profile is IFedevAuthUserProfile => profile !== null
          ),
          take(1),
          switchMap((userProfile) =>
            this.usersService
              .getCurrentUser()
              .pipe(map((user) => this.buildLoadUserSuccess(userProfile, user)))
          ),
          catchError((error) =>
            of(loadCurrentUserFailure({ error, loading: false }))
          )
        )
      )
    )
  );

  public readonly syncActiveLanguage$ = createEffect(
    () =>
      this.store.select(selectActiveLanguage).pipe(
        filter((language): language is string => !!language),
        map((language) => language.toLowerCase()),
        distinctUntilChanged(),
        switchMap((language) => this.translate.use(language))
      ),
    { dispatch: false }
  );

  private buildLoadUserSuccess(
    userProfile: IFedevAuthUserProfile,
    user: IUserProfile
  ): ReturnType<typeof loadCurrentUserSuccess> {
    const oidcSub = userProfile['sub'] || '';
    const backendQorCNumber = user.qorCNumber || '';
    const isImpersonating =
      !!oidcSub && !!backendQorCNumber && oidcSub !== backendQorCNumber;
    return loadCurrentUserSuccess({
      currentUser: {
        profile: { ...this.mapUserData(userProfile) },
        loading: false,
        user,
        impersonating: isImpersonating,
        impersonatedUserName: isImpersonating
          ? `${user.firstName} ${user.lastName} (${backendQorCNumber})`
          : null,
      },
    });
  }

  public switchCountry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(switchCountry),
      switchMap(({ countryId }) =>
        this.usersService.switchCountry(countryId).pipe(
          map((user) => switchCountrySuccess({ user })),
          catchError((error) => of(switchCountryFailure({ error })))
        )
      )
    )
  );

  public switchCountryRefresh$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(switchCountrySuccess),
        tap(({ user }) => {
          dispatchViewableQueueSummaries(this.store, user.roles);
          this.reloadCurrentRoute();
        })
      ),
    { dispatch: false }
  );

  private readonly reloadCurrentRoute = (): void => {
    const url = this.router.url;
    const shouldReuseRoute = this.router.routeReuseStrategy.shouldReuseRoute;
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    void this.router
      .navigateByUrl(url, { onSameUrlNavigation: 'reload' })
      .finally(() => {
        this.router.routeReuseStrategy.shouldReuseRoute = shouldReuseRoute;
      });
  };

  public switchLanguage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(switchLanguage),
        switchMap(({ language }) =>
          this.usersService.switchLanguage(language.toUpperCase())
        )
      ),
    { dispatch: false }
  );

  public startImpersonation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startImpersonation),
      switchMap(({ userId, userName }) =>
        this.usersService.startImpersonateUser(userId).pipe(
          tap(() => this.router.navigate(['/dashboard'])),
          switchMap(() => [
            startImpersonationSuccess({ userName }),
            loadCurrentUser(),
          ]),
          catchError((error) => of(startImpersonationFailure({ error })))
        )
      )
    )
  );

  public stopImpersonation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(stopImpersonation),
      switchMap(() =>
        this.usersService.stopImpersonateUser().pipe(
          tap(() => this.router.navigate(['/dashboard'])),
          switchMap(() => [stopImpersonationSuccess(), loadCurrentUser()]),
          catchError((error) => of(stopImpersonationFailure({ error })))
        )
      )
    )
  );
}
